/**
 * Correlation ID utility for frontend-to-backend request tracing
 * Generates UUID v4 correlation IDs for end-to-end request tracking
 */

/**
 * Generate a new correlation ID for request tracing.
 * Uses crypto.randomUUID() in browsers that support it (all modern browsers).
 * Falls back to timestamp + random string for older browsers.
 *
 * @returns {string} UUID v4 correlation ID
 *
 * @example
 * const correlationId = generateCorrelationId();
 * // => 'a1b2c3d4-e5f6-4890-abcd-ef1234567890'
 */
export function generateCorrelationId() {
  // Modern browsers (Chrome 92+, Firefox 95+, Safari 15.4+)
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }

  // Fallback for older browsers using crypto.getRandomValues (more secure than Math.random)
  // crypto.getRandomValues is supported in all browsers that support ES6 (IE 11+)
  // Format: timestamp-randomHex (e.g., '1704067200000-a1b2c3d4e5f6g7h8')
  const timestamp = Date.now();
  const randomBytes = new Uint8Array(8);
  crypto.getRandomValues(randomBytes);
  const randomHex = Array.from(randomBytes)
    .map(b => b.toString(16).padStart(2, '0'))
    .join('');
  return `${timestamp}-${randomHex}`;
}

/**
 * Get or create a session-level correlation ID that persists across multiple requests
 * within the same browser tab. Session ID is cleared when the tab is closed.
 *
 * Used for tracking user sessions (multiple requests from same user) while each
 * individual request has its own unique correlation ID for detailed tracing.
 *
 * @returns {string} Session correlation ID (UUID v4)
 *
 * @example
 * const sessionId = getSessionCorrelationId();
 * // => 'b2c3d4e5-f6g7-4890-hijk-lm9012345678'
 * // Same sessionId returned on subsequent calls within this tab
 */
export function getSessionCorrelationId() {
  const SESSION_KEY = 'rbc_session_correlation_id';

  // Try to retrieve existing session ID
  let sessionId = sessionStorage.getItem(SESSION_KEY);

  // Generate new session ID if none exists
  if (!sessionId) {
    sessionId = generateCorrelationId();
    sessionStorage.setItem(SESSION_KEY, sessionId);
  }

  return sessionId;
}
